import { useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { auth } from '@/lib/firebase';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Body, Caption, SubHeading, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { subscribeToUserTrips, type Trip } from '@/services/trips';
import { fetchOpenMeteoCurrentWeather } from '@/services/weather';
import { resolveTripWeatherLocation, type TripWeatherLocation } from '@/utils/tripWeatherLocation';
import { getTripDayContext } from '@/utils/tripDayContext';

const pickDebugWeatherTrip = (trips: Trip[]): Trip | null => {
  const candidates = trips.filter((trip) => !trip.isArchived);
  if (!candidates.length) return null;

  const active = candidates.find((trip) => getTripDayContext(trip).state === 'active');
  if (active) return active;

  const upcoming = candidates
    .filter((trip) => getTripDayContext(trip).state === 'upcoming')
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  if (upcoming.length) return upcoming[0];

  return candidates.sort((a, b) => b.startDate.getTime() - a.startDate.getTime())[0] ?? null;
};

const formatCoords = (latitude?: number, longitude?: number): string => {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return 'none';
  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
};

export function WeatherLocationDebugCard() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [resolvedLocation, setResolvedLocation] = useState<TripWeatherLocation | null>(null);
  const [liveTemp, setLiveTemp] = useState<number | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const debugTrip = useMemo(() => pickDebugWeatherTrip(trips), [trips]);

  const storedCoords = useMemo(() => {
    if (!debugTrip) return null;
    const context = getTripDayContext(debugTrip);
    const destination = context.activeDestination ?? debugTrip.destinations?.[0] ?? null;
    if (destination?.coordinates) {
      return {
        label: 'destination.coordinates',
        latitude: destination.coordinates.latitude,
        longitude: destination.coordinates.longitude,
      };
    }
    if (debugTrip.destinationCoordinates) {
      return {
        label: 'trip.destinationCoordinates',
        latitude: debugTrip.destinationCoordinates.latitude,
        longitude: debugTrip.destinationCoordinates.longitude,
      };
    }
    return null;
  }, [debugTrip]);

  const refreshWeather = useCallback(async (trip: Trip | null) => {
    if (!trip) {
      setResolvedLocation(null);
      setLiveTemp(null);
      return;
    }

    const location = await resolveTripWeatherLocation(trip);
    setResolvedLocation(location);
    if (!location) {
      setLiveTemp(null);
      return;
    }

    const live = await fetchOpenMeteoCurrentWeather(location.latitude, location.longitude);
    setLiveTemp(typeof live?.temperature === 'number' ? live.temperature : null);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await refreshWeather(debugTrip);
    } finally {
      setLastCheckedAt(new Date());
      setLoading(false);
    }
  }, [debugTrip, refreshWeather]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setTrips([]);
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToUserTrips(uid, (nextTrips) => {
      setTrips(nextTrips);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <Card variant="outlined" style={{ borderColor: theme.colors.primary + '55' }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) }}>
        <Ionicons name="cloud-outline" size={18} color={theme.colors.primary} />
        <SubHeading>Weather location QA</SubHeading>
      </View>
      <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
        Shows which coordinates Trips and Plan weather use for your active trip.
      </Body>

      <View style={{ gap: theme.spacing(0.75), marginBottom: theme.spacing(1.5) }}>
        <Caption color={theme.colors.textSecondary}>
          Trip: {debugTrip?.name ?? 'none'}
        </Caption>
        <Caption color={theme.colors.textSecondary}>
          Destination label: {debugTrip?.destination || debugTrip?.destinations?.[0]?.city || 'none'}
        </Caption>
        <Caption color={theme.colors.textSecondary}>
          Stored coords ({storedCoords?.label ?? 'none'}):{' '}
          {formatCoords(storedCoords?.latitude, storedCoords?.longitude)}
        </Caption>
        <Caption color={theme.colors.textSecondary}>
          Resolved label: {resolvedLocation?.label ?? 'unresolved'}
        </Caption>
        <Caption color={theme.colors.textSecondary}>
          Resolved coords: {formatCoords(resolvedLocation?.latitude, resolvedLocation?.longitude)}
        </Caption>
        <Caption color={theme.colors.textSecondary}>
          Country code: {resolvedLocation?.countryCode ?? 'none'}
        </Caption>
        <ThemedText variant="caption" color={theme.colors.text}>
          Source: {resolvedLocation?.source ?? 'n/a'}
          {typeof liveTemp === 'number' ? ` · Open-Meteo now: ${liveTemp}°C` : ''}
        </ThemedText>
        {lastCheckedAt ? (
          <Caption color={theme.colors.textSecondary}>
            Last checked {lastCheckedAt.toLocaleTimeString()}
          </Caption>
        ) : null}
      </View>

      <Button
        label={loading ? 'Refreshing weather debug...' : 'Refresh weather debug'}
        variant="secondary"
        disabled={loading}
        onPress={() => void refresh()}
        icon={<Ionicons name="refresh-outline" size={18} color={theme.colors.text} />}
      />
    </Card>
  );
}
